import { z } from "zod";
import { optionalUnlessRequired, type FieldDescriptor } from "./types.js";

// NG format dd/mm/yyyy is a *display* concern; ISO is what is stored (docs/01-PRODUCT-SPEC.md
// §3.2, and CLAUDE.md: never do date arithmetic on strings).
const configSchema = z.object({
  min: z.string().optional(), // ISO yyyy-mm-dd
  max: z.string().optional(),
  restriction: z.enum(["none", "past_only", "future_only"]).default("none"),
});

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const dateField: FieldDescriptor<z.infer<typeof configSchema>> = {
  type: "date",
  label: "Date",
  category: "date_time",
  icon: "Calendar",
  configSchema,
  valueSchema: (config, isRequired) => {
    const base = z
      .string()
      .regex(ISO_DATE, "Enter a valid date")
      .refine((value) => !Number.isNaN(Date.parse(value)), "Enter a valid date")
      .superRefine((value, ctx) => {
        // Compared as UTC midnight on both sides — no local-timezone drift across the boundary.
        const asDate = new Date(`${value}T00:00:00Z`);
        const todayIso = new Date().toISOString().slice(0, 10);
        const today = new Date(`${todayIso}T00:00:00Z`);

        if (config.min && asDate < new Date(`${config.min}T00:00:00Z`)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Must be on or after ${config.min}`,
          });
        }
        if (config.max && asDate > new Date(`${config.max}T00:00:00Z`)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Must be on or before ${config.max}`,
          });
        }
        if (config.restriction === "past_only" && asDate > today) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Must be in the past" });
        }
        if (config.restriction === "future_only" && asDate < today) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Must be in the future" });
        }
      });

    return optionalUnlessRequired(base, isRequired);
  },
  defaultConfig: { restriction: "none" },
  supportsProfileBinding: true,
  defaultSearchable: false,
  sortKind: "temporal",
  renderHints: {},
};
